import { buildClasses } from "../utils/buildClasses";
import { useEffect, useState } from "react";
import { RyeBadge } from "./RyeBadge";
import { RyeVariant } from "./RyeConsts";

export function RyeToggle({
  initIsChecked,
  onChange,
  disabled = false,
  label = undefined,
  variant = "outlined",
  size = "md",
}: {
  initIsChecked: boolean;
  onChange: (newState: boolean) => void;
  disabled?: boolean;
  label?: string;
  variant?: RyeVariant;
  size?: "xs" | "sm" | "md" | "lg";
}): JSX.Element {
  const [isChecked, setIsChecked] = useState(initIsChecked);

  useEffect(() => {
    setIsChecked(initIsChecked);
  }, [initIsChecked]);

  function handleClick() {
    if (disabled) {
      return;
    }
    onChange(!isChecked);
    setIsChecked((prevState) => !prevState);
  }

  const toggle = (
    <div
      className={buildClasses(
        {
          if: isChecked,
          then: buildClasses("bg-purple-500"),
          else: buildClasses("bg-well-strong"),
        },
        "rounded-full",
        {
          switch: size,
          cases: new Map([
            ["xs", "p-0.5"],
            ["sm", "p-0.5"],
            ["md", "p-1"],
            ["lg", "p-1.5"],
          ]),
        },
        "flex",
        "cursor-pointer",
        "transition-colors",
        {
          switch: size,
          cases: new Map([
            ["xs", "w-7"],
            ["sm", "w-9"],
            ["md", "w-11"],
            ["lg", "w-14"],
          ]),
        },
      )}
      onClick={handleClick}
    >
      <TransitioningSpacer isExpanded={isChecked} size={size} />
      <div
        className={buildClasses(
          {
            switch: size,
            cases: new Map([
              ["xs", buildClasses("w-2.5", "h-2.5")],
              ["sm", buildClasses("w-3.5", "h-3.5")],
              ["md", buildClasses("w-4", "h-4")],
              ["lg", buildClasses("w-5", "h-5")],
            ]),
          },
          "bg-surface-50",
          "shadow-raised",
          "rounded-full",
          "flex-shrink-0",
        )}
      />
      <TransitioningSpacer isExpanded={!isChecked} size={size} />
    </div>
  );
  if (label) {
    return (
      <RyeBadge
        text={label}
        variant={variant}
        shape="fully-round"
        vibe="subdued"
        size={variant === "outlined" ? "md" : "sm"}
        className="ml-2 mr-2"
      >
        {toggle}
      </RyeBadge>
    );
  }

  return toggle;
}

function TransitioningSpacer(props: {
  isExpanded: boolean;
  size: "xs" | "sm" | "md" | "lg";
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        {
          if: props.isExpanded,
          then: buildClasses({
            switch: props.size,
            cases: new Map([
              ["xs", "w-3"],
              ["sm", "w-5"],
              ["md", "w-5"],
              ["lg", "w-6"],
            ]),
          }),
          else: "w-0",
        },
        "transition-all",
      )}
    ></div>
  );
}
